import { clsx } from "clsx";
import { twMerge } from "tailwind-merge"

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// ==================== BARCODE NORMALIZATION ====================
/**
 * Normalize barcode: remove spaces and limit to 16 characters
 * Applied only to NEW entries, not retroactively to existing data
 * @param {string} code - Raw barcode input
 * @returns {string} Normalized barcode (no spaces, max 16 chars, uppercase)
 */
export function normalizeBarcode(code) {
  if (!code) return '';
  return code
    .replace(/\s/g, '')  // Remove all spaces
    .slice(0, 16)        // Limit to 16 characters
    .toUpperCase();      // Convert to uppercase
}

// Capitalize first letter of string
export function capitalizeFirst(str) {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
}

// ==================== TVA CALCULATIONS (21%) ====================
// Prețurile sunt CU TVA inclus

const TVA_RATE = 0.21;

// ==================== PRICE ROUNDING ====================
/**
 * Round price UP if decimal part is >= 0.90 (ends in .9X)
 * Examples: 9.99 → 10, 9.97 → 10, 9.90 → 10, 9.89 → 9.89
 * @param {number} price - Price to round
 * @returns {number} Rounded price
 */
export function roundPrice(price) {
  if (!price || price <= 0) return 0;
  const decimal = price % 1; // Get decimal part
  if (decimal >= 0.90) {
    return Math.ceil(price); // Round up to next integer
  }
  return price;
}

/**
 * Calculate line total with rounding (qty x price, rounded if ends in .9X)
 * @param {number} qty - Quantity
 * @param {number} unitPrice - Unit price
 * @returns {number} Rounded line total
 */
export function calcLineTotal(qty, unitPrice) {
  const total = (qty || 0) * (unitPrice || 0);
  return roundPrice(total);
}

/**
 * Format price with comma as decimal separator
 * @param {number} value - Price value
 * @returns {string} Formatted price
 */
export function formatPrice(value) {
  if (value === undefined || value === null) return '0,00';
  const rounded = roundPrice(value);
  return rounded.toFixed(2).replace('.', ',');
}

/**
 * Calculate price without TVA from total price (with TVA included)
 * @param {number} totalWithTVA - Price with TVA included
 * @returns {number} Price without TVA
 */
export function getPriceWithoutTVA(totalWithTVA) {
  if (!totalWithTVA || totalWithTVA <= 0) return 0;
  return totalWithTVA / (1 + TVA_RATE);
}

/**
 * Calculate TVA amount from total price (with TVA included)
 * @param {number} totalWithTVA - Price with TVA included
 * @returns {number} TVA amount
 */
export function getTVAAmount(totalWithTVA) {
  if (!totalWithTVA || totalWithTVA <= 0) return 0;
  return totalWithTVA - getPriceWithoutTVA(totalWithTVA);
}

/**
 * Get all TVA calculations from a total price
 * @param {number} totalWithTVA - Price with TVA included
 * @returns {{ priceWithoutTVA: number, tva: number, total: number }}
 */
export function calculateTVA(totalWithTVA) {
  const priceWithoutTVA = getPriceWithoutTVA(totalWithTVA);
  const tva = getTVAAmount(totalWithTVA);
  return {
    priceWithoutTVA,
    tva,
    total: totalWithTVA
  };
}

/**
 * Format TVA breakdown as string
 * @param {number} totalWithTVA - Price with TVA included
 * @returns {string} Formatted string
 */
export function formatTVABreakdown(totalWithTVA) {
  const { priceWithoutTVA, tva, total } = calculateTVA(totalWithTVA);
  return `Fără TVA: ${priceWithoutTVA.toFixed(2)} | TVA: ${tva.toFixed(2)} | Total: ${total.toFixed(2)}`;
}

// Sound functions for notifications
export const playSuccessSound = () => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    // Pleasant ascending tone for success
    oscillator.frequency.value = 800;
    oscillator.type = 'sine';
    gainNode.gain.setValueAtTime(0.2, audioContext.currentTime);
    
    // Quick ascending beep
    oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
    oscillator.frequency.setValueAtTime(1200, audioContext.currentTime + 0.08);
    
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.15);
    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.15);
  } catch (err) {
    console.log('Sound not supported');
  }
};

export const playErrorSound = () => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    // Lower, descending buzz for error
    oscillator.frequency.value = 400;
    oscillator.type = 'square'; // Harsher sound
    gainNode.gain.setValueAtTime(0.15, audioContext.currentTime);
    
    // Two quick low beeps
    oscillator.frequency.setValueAtTime(400, audioContext.currentTime);
    oscillator.frequency.setValueAtTime(300, audioContext.currentTime + 0.1);
    
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.25);
    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.25);
  } catch (err) {
    console.log('Sound not supported');
  }
};
